import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils/cn";

/** A small, dependency-free tooltip for icon-only affordances that have no
 * visible label - per the interaction spec, not for buttons that already
 * show text. Shows after a short delay (avoids flashing on a fast mouse
 * pass-through), fades + nudges up on entrance, and is keyboard-reachable
 * via the same focus the wrapped control already receives (no separate
 * tabIndex - this wraps the control, it doesn't intercept its focus).
 *
 * Deliberately does not replace the native `title=` attribute app-wide
 * (80+ existing call sites already rely on it as a working, if plain,
 * tooltip) - this is for a bounded set of icon-only row actions where the
 * richer, theme-consistent version is worth the extra component. */
export function Tooltip({ label, children, side = "top" }: { label: string; children: ReactNode; side?: "top" | "bottom" }) {
  const [show, setShow] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  // A pending show must not outlive the tooltip: firing setShow after
  // unmount (a row removed mid-hover, or a test torn down within the
  // 400ms) updates an unmounted component - in CI it threw "window is not
  // defined" after jsdom was gone.
  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  function scheduleShow() {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setShow(true), 400);
  }
  function hide() {
    clearTimeout(timeoutRef.current);
    setShow(false);
  }

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={scheduleShow}
      onMouseLeave={hide}
      onFocus={scheduleShow}
      onBlur={hide}
    >
      {children}
      <span
        role="tooltip"
        className={cn(
          "pointer-events-none absolute left-1/2 z-30 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-lg transition-all duration-150 ease-out",
          side === "top" ? "bottom-full mb-1.5" : "top-full mt-1.5",
          show ? "translate-y-0 opacity-100" : cn(side === "top" ? "translate-y-1" : "-translate-y-1", "opacity-0"),
        )}
      >
        {label}
      </span>
    </span>
  );
}
